/**
 * Inactivity auto-logout.
 *
 * Signs the user out after a configurable stretch of no activity. The timeout
 * (minutes) comes from the Security & Access policy (GET /auth/security-settings)
 * and any genuine interaction resets the countdown. Only armed while a session
 * exists, so the login screen is never affected.
 *
 * Deliberately does NOT use react-admin's useLogout: this hook runs in
 * AdminContent, which renders <Admin> and therefore sits *outside* the
 * QueryClientProvider that react-admin sets up inside <Admin>. Calling a
 * react-query-backed hook here throws "No QueryClient set". We log out through
 * the auth provider directly instead, which needs no React context.
 */
import { useEffect, useRef } from 'react';
import authProvider from '../authProvider';
import { apiClient } from '../services/api';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
const DEFAULT_MINUTES = 30;

const isAuthenticated = (): boolean => {
  try {
    return !!localStorage.getItem('user');
  } catch {
    return false;
  }
};

export function useIdleLogout(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minutesRef = useRef<number>(DEFAULT_MINUTES);

  useEffect(() => {
    // Don't arm (or hit the API) on the login screen — only for a live session.
    if (!isAuthenticated()) return;

    let disposed = false;

    const fire = () => {
      if (!isAuthenticated()) return;
      try {
        sessionStorage.setItem('logout_reason', 'idle');
      } catch { /* storage may be blocked; not essential */ }
      Promise.resolve(authProvider.logout ? authProvider.logout({}) : undefined)
        .catch(() => { /* fall through to the redirect regardless */ })
        .finally(() => { window.location.href = '/login'; });
    };

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fire, minutesRef.current * 60 * 1000);
    };

    const onActivity = () => reset();

    // Load the configured timeout, then arm. Falls back to the default if the
    // policy can't be read.
    apiClient
      .get<{ idle_timeout_minutes?: number }>('/auth/security-settings')
      .then((r) => {
        const m = Number(r?.data?.idle_timeout_minutes);
        if (!disposed && Number.isFinite(m) && m > 0) minutesRef.current = m;
      })
      .catch(() => { /* keep the default */ })
      .finally(() => { if (!disposed) reset(); });

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onActivity);
    reset();

    return () => {
      disposed = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onActivity);
    };
  }, []);
}
